import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import _, { isArray } from 'lodash';
import DiaLog from '../../../../components/Dialog';
import { ADS_TYPE } from '../../../../comm/constant';
import { AutoCompleteSelect } from '../../../../components';
import { equipmentAPI, systemAPI } from '../../../../apis';
import { useGetUserInfo } from '../../../../utils';
import { i18n } from '../../../../language';

const useStyles = makeStyles(() => ({
  adsPopContent: ({ type }) => ({
    fontSize: '1rem',
    color: '#2e4469',
    fontFamily: 'NotoSans-Medium',
    paddingBottom: type === ADS_TYPE.MULTIPLE ? '1.5rem' : '0',
    '&>span': {
      color: '#ff655c',
    },
  }),
}));

const SingleADSDialog = forwardRef(
  (
    {
      handleClose = () => {},
      handleNewRequest,
      equipmentAssets,
      formData,
      adsType = 'new',
      getAdsOrderParams,
      handleDelete,
      adsWardInfo,
      adsPopup,
      provideWardId,
      requestWard,
      adsWardLocationId,
      categorySatelliteStoresId,
      transferSatelliteStoresId,
      editEquipment = false,
      currentLocationText,
      assetId,
    },
    ref
  ) => {
    const [type, setType] = useState('');
    const classes = useStyles({ type });
    const [serviceType, setServiceType] = useState('');
    const [open, setOpen] = useState(false);
    const [locationInfo, setLocationInfo] = useState({});
    const [adsOrder, setAdsOrder] = useState('');
    const [serviceTypeList, setServiceTypeList] = useState([]);
    const [wardLocation, setWardLocation] = useState([]);
    const userInfo = useGetUserInfo();
    const hospitalCategoryId = equipmentAssets?.[0]?.category || '';

    const getWardLocation = useCallback(async () => {
      try {
        const { result } = await systemAPI.getAdsList({ hospitalCode: userInfo.hospCode });
        setWardLocation(result.ADSInfoDetails);
      } catch (error) {
        console.log(error);
      }
    }, []);

    const getAdsLocation = useCallback(async () => {
      try {
        const locationParams = {
          providerWard: provideWardId,
          requestWard: requestWard,
          adsWardLocationId,
          categorySatelliteStoresId,
          hospitalCategoryId,
          loanType: editEquipment ? 'SATELLITESTORES' : 'BORROW',
          transferSatelliteStoresId,
          assetId,
        };
        const serviceTypeParams = {
          hospitalCategoryIds: _.map(equipmentAssets, (item) => item.category),
        };
        const hasAsset = _.some(equipmentAssets, (item) => item?.assets?.length > 0);
        const { result } = await equipmentAPI.getAdsLocation(locationParams);
        const { result: serviceTypeResult } = await equipmentAPI.getServiceType(serviceTypeParams);
        serviceTypeResult.CategoryServiceType = _.uniqWith(
          serviceTypeResult.CategoryServiceType,
          (a, b) => a.adsBorrowServiceType === b.adsBorrowServiceType
        );
        await getWardLocation();
        setLocationInfo({ ...result, ...serviceTypeResult });

        // 如果是编辑设备，特殊处理
        if (editEquipment) {
          if (serviceTypeResult?.CategoryServiceType?.[0]?.satelliteStoresTransferEnableYn === 1) {
            setOpen(true);
            if (!(result.fromLocation && result.toLocation)) {
              // missing location
              setType(ADS_TYPE.MISSING);
            } else if (serviceTypeResult.CategoryServiceType.length === 1) {
              // 只有一条 service type
              setType(ADS_TYPE.SINGLE);
              setServiceType(serviceTypeResult.CategoryServiceType[0].hospitalCategoryId);
            } else {
              // 有多条 service type
              setType(ADS_TYPE.MULTIPLE);
            }
          } else {
            // 直接更新
            handleNewRequest({ adsCategoryId: '', isAdsServer: 'FALSE' });
            handleClose();
          }
          return;
        }

        // new request 的触发条件
        const newCondition =
          adsType === 'new' &&
          serviceTypeResult?.CategoryServiceType?.[0]?.adsBorrowEnableYn === 1 &&
          (serviceTypeResult?.CategoryServiceType?.[0]?.adsCreationTigger === 1 ||
            (serviceTypeResult?.CategoryServiceType?.[0]?.adsCreationTigger === 2 && hasAsset));

        // update request 的触发条件
        const updateCondition =
          adsType !== 'new' &&
          serviceTypeResult?.CategoryServiceType?.[0]?.adsBorrowEnableYn === 1 &&
          serviceTypeResult?.CategoryServiceType?.[0]?.adsCreationTigger === 2 &&
          formData.status === 1;

        if (newCondition || updateCondition) {
          setOpen(true);
          if (!(result.fromLocation && result.toLocation)) {
            // missing location
            setType(ADS_TYPE.MISSING);
          } else if (serviceTypeResult.CategoryServiceType.length === 1) {
            // 只有一条 service type
            setType(ADS_TYPE.SINGLE);
            setServiceType(serviceTypeResult.CategoryServiceType[0].hospitalCategoryId);
          } else {
            // 有多条 service type
            setType(ADS_TYPE.MULTIPLE);
          }
        } else {
          // 没有service type，直接创建
          handleNewRequest({ adsCategoryId: '', isAdsServer: 'FALSE' });
          handleClose();
        }
      } catch (error) {
        console.log(error);
        handleClose();
      }
    }, [equipmentAssets, formData, getWardLocation, provideWardId]);

    const getAdsLastOrder = useCallback(async () => {
      try {
        const { result } = await equipmentAPI.getAdsOrder(getAdsOrderParams);
        if (!result?.latestOrderNo) {
          handleDelete({ isAdsServer: 'FALSE' });
          return;
        }
        setAdsOrder(result.latestOrderNo);
        if (result.latestOrderStatus === 'C') {
          setType(adsType === 'withdraw' ? ADS_TYPE.REMINDER_WITHDRAWN : ADS_TYPE.REMINDER_DECLINED);
        } else {
          setType(adsType === 'withdraw' ? ADS_TYPE.UPDATE_WITHDRAWN : ADS_TYPE.UPDATE_DECLINED);
        }
        setOpen(true);
      } catch (error) {
        handleClose();
        console.log(error);
      }
    }, [getAdsOrderParams, adsType]);

    useImperativeHandle(ref, () => ({
      getAdsLocation,
      getAdsLastOrder,
    }));

    useEffect(() => {
      if (adsPopup === false) {
        // 依赖旧有属性，初始化state
        setType('');
        setServiceType('');
        setOpen(false);
        setLocationInfo({});
        setAdsOrder('');
        setServiceTypeList([]);
        setWardLocation([]);
      }
    }, [adsPopup]);

    const locationDesc = useMemo(() => {
      let provider = '';
      let request = '';

      if (isArray(wardLocation) && wardLocation.length > 0) {
        provider = _.find(wardLocation, (item) => item.locationCode === locationInfo?.fromLocation)?.locationDesc;
        request = _.find(wardLocation, (item) => item.locationCode === locationInfo?.toLocation)?.locationDesc;
      }

      return {
        provider,
        request,
      };
    }, [wardLocation, locationInfo, adsWardLocationId, categorySatelliteStoresId]);

    const copywriting = useMemo(() => {
      switch (type) {
        case ADS_TYPE.SINGLE:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_CREATE_TITLE'),
            content: i18n.t(
              editEquipment
                ? 'EQUIPMENT_ADS_POPUP_TRANSFER_SATELLITE_SINGLE_CONTENT'
                : 'EQUIPMENT_ADS_POPUP_CREATE_SINGLE_CONTENT',
              {
                provider: locationDesc.provider,
                request: locationDesc.request,
                serviceType: _.find(
                  serviceTypeList,
                  (item) =>
                    item?.serviceCid ===
                    (editEquipment
                      ? locationInfo?.CategoryServiceType?.[0]?.satelliteStoresTransferServiceType
                      : locationInfo?.CategoryServiceType?.[0]?.adsBorrowServiceType)
                )?.serviceDesc,
              }
            ),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_SKIP'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_CONFIRM'),
          };
        case ADS_TYPE.MULTIPLE:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_CREATE_TITLE'),
            content: i18n.t(
              editEquipment
                ? 'EQUIPMENT_ADS_POPUP_TRANSFER_SATELLITE_MULTIPLE_CONTENT'
                : 'EQUIPMENT_ADS_POPUP_CREATE_MULTIPLE_CONTENT',
              {
                provider: locationDesc.provider,
                request: locationDesc.request,
              }
            ),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_SKIP'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_CONFIRM'),
          };
        case ADS_TYPE.MISSING:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_CREATE_MISSING_TITLE'),
            content: i18n.t(
              editEquipment
                ? 'EQUIPMENT_ADS_POPUP_TRANSFER_SATELLITE_MISSING_CONTENT'
                : 'EQUIPMENT_ADS_POPUP_CREATE_MISSING_CONTENT',
              {
                provider: locationDesc.provider ?? '<span>N/A</span>',
                request: locationDesc.request ?? '<span>N/A</span>',
                missing: locationInfo?.missingLocation?.join(', '),
                currentLocation: editEquipment ? currentLocationText : '',
                // missing: editEquipment
                //   ? locationInfo?.missingLocation?.join(', ')
                //   : [
                //       locationDesc.provider ? '' : adsWardInfo?.provider,
                //       locationDesc.request ? '' : adsWardInfo?.request,
                //     ]
                //       .filter((item) => item)
                //       .join(','),
              }
            ),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_NO'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_YES'),
          };
        case ADS_TYPE.UPDATE_DECLINED:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_UPDATE_TITLE'),
            content: i18n.t('EQUIPMENT_ADS_POPUP_UPDATE_CONTENT_DECLINE', { value: adsOrder }),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_CANCEL'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_CONFIRM'),
          };
        case ADS_TYPE.UPDATE_WITHDRAWN:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_UPDATE_TITLE'),
            content: i18n.t('EQUIPMENT_ADS_POPUP_UPDATE_CONTENT_WITHDRAW', { value: adsOrder }),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_CANCEL'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_CONFIRM'),
          };
        case ADS_TYPE.REMINDER_DECLINED:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_REMINDER_TITLE'),
            content: i18n.t('EQUIPMENT_ADS_POPUP_REMINDER_CONTENT_DECLINE'),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_NO'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_YES'),
          };
        case ADS_TYPE.REMINDER_WITHDRAWN:
          return {
            title: i18n.t('EQUIPMENT_ADS_POPUP_REMINDER_TITLE'),
            content: i18n.t('EQUIPMENT_ADS_POPUP_REMINDER_CONTENT_WITHDRAW'),
            cancel: i18n.t('EQUIPMENT_ADS_POPUP_NO'),
            confirm: i18n.t('EQUIPMENT_ADS_POPUP_YES'),
          };
        default:
          return {
            title: '',
            content: '',
            confirm: '',
          };
      }
    }, [type, locationInfo, adsOrder, serviceTypeList, locationDesc, adsWardInfo, editEquipment, editEquipment]);

    const itemList = useMemo(() => {
      if (
        isArray(serviceTypeList) &&
        serviceTypeList.length > 0 &&
        isArray(locationInfo.CategoryServiceType) &&
        locationInfo.CategoryServiceType.length > 0
      ) {
        return _.map(locationInfo.CategoryServiceType, (item) => ({
          text:
            _.find(serviceTypeList, (typeItem) => typeItem.serviceCid === item.adsBorrowServiceType)?.serviceDesc ?? '',
          value: item.hospitalCategoryId,
        }));
      } else {
        return [];
      }
    }, [locationInfo.CategoryServiceType, serviceTypeList]);

    const handleConfirm = useCallback(() => {
      try {
        switch (type) {
          case ADS_TYPE.SINGLE:
          case ADS_TYPE.MULTIPLE:
            handleNewRequest({ adsCategoryId: serviceType, isAdsServer: 'TRUE' });
            break;
          case ADS_TYPE.MISSING:
            handleNewRequest({ adsCategoryId: '', isAdsServer: 'FALSE' });
            break;
          case ADS_TYPE.UPDATE_DECLINED:
          case ADS_TYPE.UPDATE_WITHDRAWN:
            handleDelete({ isAdsServer: 'TRUE' });
            break;
          case ADS_TYPE.REMINDER_WITHDRAWN:
          case ADS_TYPE.REMINDER_DECLINED:
            handleDelete({ isAdsServer: 'FALSE' });
            break;
          default:
            break;
        }
      } catch (error) {
        console.log(error);
      }
    }, [handleNewRequest, serviceType, type, handleClose, handleDelete]);

    const handleCancel = useCallback(() => {
      try {
        switch (type) {
          case ADS_TYPE.SINGLE:
          case ADS_TYPE.MULTIPLE:
            handleNewRequest({ adsCategoryId: '', isAdsServer: 'FALSE' });
            break;
          case ADS_TYPE.MISSING:
          case ADS_TYPE.UPDATE_DECLINED:
          case ADS_TYPE.UPDATE_WITHDRAWN:
          case ADS_TYPE.REMINDER_WITHDRAWN:
          case ADS_TYPE.REMINDER_DECLINED:
            handleClose();
            break;
          default:
            break;
        }
      } catch (error) {
        console.log(error);
      }
    }, [handleNewRequest, type, handleClose]);

    const disableConfirm = useMemo(() => {
      return type === ADS_TYPE.MULTIPLE && !serviceType;
    }, [type, serviceType]);

    const getServiceType = useCallback(async () => {
      try {
        const { result } = await systemAPI.getAdsServiceType({ hospitalCode: userInfo.hospCode });
        setServiceTypeList(result.ADSInfoDetails);
      } catch (error) {
        console.log(error);
      }
    }, []);

    useEffect(() => {
      if (type === ADS_TYPE.MULTIPLE || type === ADS_TYPE.SINGLE) {
        (async () => {
          await getServiceType();
        })();
      }
    }, [getServiceType, type]);

    return (
      <DiaLog
        open={open}
        title={copywriting.title}
        cancel={copywriting.cancel}
        confirm={copywriting.confirm}
        handleClose={handleClose}
        handleCancel={handleCancel}
        handleConfirm={handleConfirm}
        cancelStyle={{ border: 'unset' }}
        dialogfootStyle={{ borderTop: '1px solid #e4e7eb', paddingTop: '1.5rem' }}
        disabled={disableConfirm}
      >
        <div className={classes.adsPopContent} dangerouslySetInnerHTML={{ __html: copywriting.content }} />
        {type === ADS_TYPE.MULTIPLE && (
          <AutoCompleteSelect
            itemList={itemList}
            value={serviceType}
            onChange={setServiceType}
            placeholder={i18n.t('EQUIPMENT_ADS_POPUP_SERVICE_TYPE_PLACEHOLDER')}
          />
        )}
      </DiaLog>
    );
  }
);

export default SingleADSDialog;
